import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  Container,
  Form,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Segment,
  Image,
  Dropdown,
  Grid,
  Menu,
} from "semantic-ui-react";
import { userApi } from "@api/users/user";
import { attendanceApi } from "@api/attendance/attendance";
import { AuthenticationRoutes } from "@routes/urls";
import { Link, useLocation } from "react-router-dom";
import {
  purposeOptions,
  getCSRFToken,
  fullScreenStyle,
  containerStyle,
  segmentStyle,
  welcomeHeaderStyle,
  subHeaderStyle,
  inputStyle,
  buttonStyle,
  dropdownStyle,
} from "./constant";

const LibraryIdAttendance = () => {
  const [libraryId, setLibraryId] = useState("");
  const [userProfile, setUserProfile] = useState(null);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [purposeOfVisit, setPurposeOfVisit] = useState("Study");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [usersCache, setUsersCache] = useState(new Map());
  const [cacheLoaded, setCacheLoaded] = useState(false);
  const [isProcessingAttendance, setIsProcessingAttendance] = useState(false);
  const [checkoutInfo, setCheckoutInfo] = useState(null);
  const [showCheckoutDuration, setShowCheckoutDuration] = useState(false);
  const inputRef = useRef(null);
  const removeTrailingSlashes = (path) => path.replace(/\/+$/, "");
  const location = useLocation();
  const activePath = removeTrailingSlashes(location.pathname);
  const signUp = activePath.includes(AuthenticationRoutes.signup);

  // Load user cache on mount
  useEffect(() => {
    loadAllUsers();
  }, []);

  // Clear messages and checkout info after timeout
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  useEffect(() => {
    if (showCheckoutDuration) {
      const timer = setTimeout(() => {
        setShowCheckoutDuration(false);
        setCheckoutInfo(null);
      }, 8000); // Show checkout info longer
      return () => clearTimeout(timer);
    }
  }, [showCheckoutDuration]);

  // Handle user lookup and auto check-in/check-out
  useEffect(() => {
    const trimmedId = libraryId.trim().toUpperCase();
    let errorTimeout;

    if (!trimmedId) {
      resetUserData();
      return;
    }

    if (cacheLoaded || trimmedId.length >= 6) {
      const user = usersCache.get(trimmedId);
      if (user) {
        setUserProfile(user);
        setError("");
        handleUserDetected(user);
      } else {
        setUserProfile(null);
        setAttendanceStatus(null);
        if (
          trimmedId.length >= 6 &&
          trimmedId.includes("-") &&
          !trimmedId.endsWith("-")
        ) {
          // Delay error to avoid showing during user typing
          errorTimeout = setTimeout(() => {
            // Re-check to prevent stale error
            if (
              libraryId.trim().toUpperCase() === trimmedId &&
              !usersCache.has(trimmedId)
            ) {
              setError(`No user found with Library ID: ${trimmedId}`);
            }
          }, 1000);
        }
      }
    }

    return () => {
      if (errorTimeout) clearTimeout(errorTimeout);
    };
  }, [libraryId, usersCache, cacheLoaded]);

  // Auto-focus input
  useEffect(() => {
    if (inputRef.current && cacheLoaded && !isProcessingAttendance) {
      inputRef.current.focus();
    }
  }, [cacheLoaded, isProcessingAttendance]);

  const resetUserData = () => {
    setUserProfile(null);
    setAttendanceStatus(null);
    setError("");
    setSuccess("");
    setPurposeOfVisit("Study");
    setIsProcessingAttendance(false);
    setCheckoutInfo(null);
    setShowCheckoutDuration(false);
  };

  const loadAllUsers = async () => {
    try {
      setInitialLoading(true);
      const cache = new Map();
      const batchSize = 100;
      const maxIterations = 50;
      let currentId = 1;

      for (let i = 0; i < maxIterations; i++) {
        const userIds = Array.from(
          { length: batchSize },
          (_, idx) => currentId + idx
        );
        const response = await userApi.getBatchUserProfiles(userIds);
        const profiles = response?.data?.profiles || {};

        let foundAny = false;
        Object.values(profiles).forEach((profile) => {
          if (profile?.library_id) {
            foundAny = true;
            cache.set(profile.library_id.toUpperCase(), profile);
          }
        });

        if (!foundAny) break;
        currentId += batchSize;
      }

      setUsersCache(cache);
      setCacheLoaded(true);
    } catch (error) {
      setError("Failed to load user database. Please refresh the page.");
    } finally {
      setInitialLoading(false);
    }
  };

  const loadAttendanceStatus = async (libraryIdToCheck) => {
    try {
      const statusResponse = await attendanceApi.getUserStatus(
        libraryIdToCheck
      );
      const status = statusResponse.data || {
        is_checked_in: false,
        session: null,
        last_session: null,
      };
      setAttendanceStatus(status);
      return status;
    } catch {
      const status = {
        is_checked_in: false,
        session: null,
        last_session: null,
      };
      setAttendanceStatus(status);
      return status;
    }
  };

  const handleUserDetected = async (user) => {
    if (isProcessingAttendance) return;

    setIsProcessingAttendance(true);
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Load current attendance status
      const currentStatus = await loadAttendanceStatus(user.library_id);

      // Auto-perform attendance action based on current status
      if (currentStatus.is_checked_in) {
        // User is checked in, automatically check them out
        await performCheckOut(user);
      } else {
        // User is not checked in, show check-in interface
        // Don't auto check-in, let user select purpose first
        setLoading(false);
      }
    } catch (err) {
      setError(
        `Failed to process attendance: ${err.message || "Please try again."}`
      );
      setLoading(false);
    } finally {
      setIsProcessingAttendance(false);
    }
  };

  const performCheckIn = async (user) => {
    const csrfToken = getCSRFToken();
    const headers = {
      "X-CSRFToken": csrfToken,
      "X-CSRF-Token": csrfToken,
      "Content-Type": "application/json",
    };

    try {
      const checkInData = {
        libraryId: user.library_id,
        userName: user.name || user.full_name || "Unknown User",
        program: user.program || "",
        year: user.year || null,
        email: user.email || "",
        user_id: user.id || user.pid || null,
        purposeOfVisit: purposeOfVisit,
      };

      await attendanceApi.checkIn(checkInData, {
        headers,
        withCredentials: true,
      });

      setSuccess(
        `${
          user.name || user.full_name
        } checked in successfully for ${purposeOfVisit}!`
      );

      // Refresh attendance status
      await loadAttendanceStatus(user.library_id);

      // Clear form after successful check-in
      setTimeout(() => {
        clearFormAndFocus();
      }, 3000);
    } catch (err) {
      setError(`Check-in failed: ${err.message || "Please try again."}`);
    } finally {
      setLoading(false);
    }
  };

  const performCheckOut = async (user) => {
    const csrfToken = getCSRFToken();
    const headers = {
      "X-CSRFToken": csrfToken,
      "X-CSRF-Token": csrfToken,
      "Content-Type": "application/json",
    };

    try {
      const response = await attendanceApi.checkOut(user.library_id, {
        headers,
        withCredentials: true,
      });

      const duration = response.data?.duration?.formatted || "N/A";
      const sessionData = response.data || {};

      // Store checkout information
      setCheckoutInfo({
        userName: user.name || user.full_name,
        duration: duration,
        checkInTime: sessionData.inTime,
        checkOutTime: sessionData.outTime || new Date().toISOString(),
        purpose: sessionData.purposeOfVisit || "N/A",
      });

      setShowCheckoutDuration(true);

      setSuccess(
        `${
          user.name || user.full_name
        } checked out successfully! Session duration: ${duration}`
      );

      // Refresh attendance status
      await loadAttendanceStatus(user.library_id);

      // Clear form after showing checkout info
      setTimeout(() => {
        clearFormAndFocus();
      }, 8000); // Wait longer to show checkout duration
    } catch (err) {
      setError(`Check-out failed: ${err.message || "Please try again."}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!userProfile || !purposeOfVisit) return;

    setLoading(true);
    setError("");
    setSuccess("");

    await performCheckIn(userProfile);
  };

  const clearFormAndFocus = () => {
    setLibraryId("");
    setPurposeOfVisit("Study");
    resetUserData();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setLibraryId(value);
    // Clear any existing errors when user is typing
    if (error && value.length > 0) {
      setError("");
    }
  };

  const handleKeyPress = (e) => {
    if (
      e.key === "Enter" &&
      userProfile &&
      attendanceStatus &&
      !attendanceStatus.is_checked_in
    ) {
      handleCheckIn();
    }
  };

  const formatDateTime = (dateString) => new Date(dateString).toLocaleString();

  // Initial Loading
  if (initialLoading) {
    return (
      <div style={fullScreenStyle}>
        <Container style={{ maxWidth: "600px", padding: "1rem" }}>
          <Segment textAlign="center" padded="very" style={segmentStyle}>
            <Header as="h2" icon color="#000">
              <Icon loading name="spinner" />
              Loading User Database...
              <Header.Subheader>
                Please wait while we load the user information.
              </Header.Subheader>
            </Header>
          </Segment>
        </Container>
      </div>
    );
  }

  const showWelcome = !libraryId.trim();
  const showUserInfo = userProfile && attendanceStatus;

  return (
    <div style={fullScreenStyle}>
      <Container style={containerStyle}>
        <div style={{ width: "100%", maxWidth: "1000px" }}>
          <Segment raised style={segmentStyle}>
            {/* Welcome Section */}
            {showWelcome && (
              <div style={{ textAlign: "center", marginBottom: "3rem" }}>
                <Header as="h1" style={welcomeHeaderStyle}>
                  <Icon name="book" style={{ color: "#225745" }} />
                  Welcome to RSU Library
                </Header>
                <div
                  style={{
                    margin: "2rem 0",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Image
                    src="/images/lrc-logo.png"
                    size="small"
                    style={{ margin: "0 1rem" }}
                  />
                  <Image
                    src="/images/university-logo.png"
                    size="small"
                    style={{ margin: "0 1rem" }}
                  />
                </div>
                <Header.Subheader style={subHeaderStyle}>
                  Scan your barcode to check in/out
                </Header.Subheader>
              </div>
            )}

            {/* Header for active session */}
            {!showWelcome && (
              <Header
                as="h2"
                textAlign="center"
                style={{ marginBottom: "2rem", color: "#225745" }}
              >
                <Icon name="calendar check" />
                RSU Library Attendance
              </Header>
            )}

            {/* Input Section */}
            <div
              style={{
                textAlign: "center",
                marginBottom: "2rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexFlow: "column wrap",
              }}
            >
              <Form>
                <Form.Field
                  style={{
                    maxWidth: "700px",
                    width: "500px",
                    margin: "0 auto",
                  }}
                >
                  <Input
                    ref={inputRef}
                    size="huge"
                    placeholder="Scan barcode (e.g., FXX-XX)"
                    value={libraryId}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    disabled={loading || !cacheLoaded}
                    autoFocus
                    style={inputStyle}
                    input={{
                      style: {
                        textAlign: "center",
                      },
                    }}
                  />
                </Form.Field>
              </Form>
              {showWelcome && (
                <Menu
                  style={{
                    backgroundColor: "#225745",
                    justifyContent: "center",
                    width: "20%",
                  }}
                >
                  <Menu.Item
                    as={Link}
                    active={signUp}
                    to={AuthenticationRoutes.signup}
                    style={{
                      border: "none",
                      width: "100%",
                      justifyContent: "center",
                      color: "#fff",
                    }}
                  >
                    For Visitor Sign Up
                  </Menu.Item>
                </Menu>
              )}
            </div>

            {/* Checkout Duration Display */}
            {showCheckoutDuration && checkoutInfo && (
              <Segment
                raised
                style={{
                  backgroundColor: "#f8fff8",
                  border: "2px solid #21ba45",
                  borderRadius: "15px",
                  margin: "2rem 0",
                  padding: "2rem",
                  textAlign: "center",
                  boxShadow: "0 4px 20px rgba(33, 186, 69, 0.3)",
                }}
              >
                <Header
                  as="h2"
                  style={{ color: "#21ba45", marginBottom: "1.5rem" }}
                >
                  <Icon name="check circle outline" />
                  Successfully Checked Out!
                </Header>

                <Grid columns={2} stackable>
                  <Grid.Column>
                    <Card
                      fluid
                      style={{
                        backgroundColor: "transparent",
                        border: "none",
                        boxShadow: "none",
                      }}
                    >
                      <Card.Content>
                        <Card.Header
                          style={{
                            fontSize: "1.2rem",
                            color: "#225745",
                            marginBottom: "1rem",
                          }}
                        >
                          <Icon name="user" />
                          {checkoutInfo.userName}
                        </Card.Header>

                        <div style={{ textAlign: "left", fontSize: "1rem" }}>
                          <div style={{ marginBottom: "0.8rem" }}>
                            <Icon name="sign in" color="green" />
                            <strong>Check-in:</strong>{" "}
                            {formatDateTime(checkoutInfo.checkInTime)}
                          </div>

                          <div style={{ marginBottom: "0.8rem" }}>
                            <Icon name="sign out" color="red" />
                            <strong>Check-out:</strong>{" "}
                            {formatDateTime(checkoutInfo.checkOutTime)}
                          </div>

                          <div style={{ marginBottom: "0.8rem" }}>
                            <Icon name="bullseye" color="blue" />
                            <strong>Purpose:</strong> {checkoutInfo.purpose}
                          </div>
                        </div>
                      </Card.Content>
                    </Card>
                  </Grid.Column>

                  <Grid.Column>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <Header
                        as="h1"
                        style={{ color: "#21ba45", marginBottom: "1rem" }}
                      >
                        <Icon name="clock" />
                        Session Duration
                      </Header>

                      <div
                        style={{
                          backgroundColor: "#21ba45",
                          color: "white",
                          padding: "1.5rem 2rem",
                          borderRadius: "20px",
                          fontSize: "2rem",
                          fontWeight: "bold",
                          textAlign: "center",
                          minWidth: "200px",
                          boxShadow: "0 4px 15px rgba(33, 186, 69, 0.4)",
                        }}
                      >
                        {checkoutInfo.duration}
                      </div>

                      <div
                        style={{
                          marginTop: "1rem",
                          fontSize: "1.1rem",
                          color: "#666",
                        }}
                      >
                        Thank you for visiting RSU Library!
                      </div>
                    </div>
                  </Grid.Column>
                </Grid>
              </Segment>
            )}

            {/* Messages */}
            {error && (
              <Message negative style={{ margin: "1rem 0" }}>
                <Message.Header>Error</Message.Header>
                <p>{error}</p>
              </Message>
            )}

            {success && (
              <Message positive style={{ margin: "1rem 0" }}>
                <Message.Header>Success</Message.Header>
                <p>{success}</p>
              </Message>
            )}

            {/* User Profile Card - Show when user found but no attendance status yet and not showing checkout duration */}
            {libraryId.trim() && !showUserInfo && !showCheckoutDuration && (
              <Card fluid style={{ backgroundColor: "transparent" }}>
                <Card.Content textAlign="center">
                  {/* Avatar */}
                  <Image
                    src={
                      userProfile?.image_url
                        ? process.env
                            .REACT_APP_INVENIO_REST_ENDPOINTS_BASE_URL +
                          userProfile.image_url
                        : "/images/user.png"
                    }
                    size="tiny"
                    circular
                    centered
                    style={{ marginBottom: "1rem" }}
                  />
                  {/* Name */}
                  <Card.Header
                    style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}
                  >
                    <Icon name="user" />
                    {userProfile
                      ? userProfile.name || userProfile.full_name
                      : "User Not Found"}
                  </Card.Header>
                  {/* Library ID */}
                  <Card.Meta style={{ fontSize: "1.1rem" }}>
                    Library ID:{" "}
                    <strong>
                      {userProfile?.library_id ||
                        libraryId.trim().toUpperCase()}
                    </strong>
                  </Card.Meta>
                  {/* Details or Error Message */}
                  {userProfile ? (
                    <Card.Description
                      style={{
                        marginTop: "1.5rem",
                        fontSize: "1.1rem",
                        textAlign: "left",
                        paddingLeft: "2rem",
                        paddingRight: "2rem",
                      }}
                    >
                      {userProfile.email && (
                        <div style={{ marginBottom: "0.8rem" }}>
                          <Icon name="mail" />
                          <strong>Email:</strong> {userProfile.email}
                        </div>
                      )}
                      {userProfile.program && (
                        <div style={{ marginBottom: "0.8rem" }}>
                          <Icon name="graduation cap" />
                          <strong>Program:</strong> {userProfile.program}
                        </div>
                      )}
                      {userProfile.year && (
                        <div style={{ marginBottom: "0.8rem" }}>
                          <Icon name="calendar" />
                          <strong>Year:</strong> {userProfile.year}
                        </div>
                      )}
                    </Card.Description>
                  ) : (
                    <Message warning size="small" style={{ marginTop: "1rem" }}>
                      <Message.Header>
                        <Icon name="exclamation triangle" />
                        No user found in the system.
                      </Message.Header>
                      <p>
                        Please verify the Library ID or contact support if you
                        believe this is an error.
                      </p>
                    </Message>
                  )}

                  {/* Loading indicator when processing */}
                  {loading && (
                    <div style={{ marginTop: "1rem" }}>
                      <Icon loading name="spinner" />
                      Processing attendance...
                    </div>
                  )}
                </Card.Content>
              </Card>
            )}

            {/* Two-column layout for User Info and Check In/Out - Hide when showing checkout duration */}
            {showUserInfo && !showCheckoutDuration && (
              <Grid columns={2} stackable style={{ margin: 0 }}>
                {/* Left Column: User Information & Purpose of Visit */}
                <Grid.Column>
                  <Card
                    fluid
                    style={{ backgroundColor: "transparent", height: "100%" }}
                  >
                    <Card.Content textAlign="center">
                      {/* Avatar */}
                      <Image
                        src={
                          userProfile?.image_url
                            ? process.env
                                .REACT_APP_INVENIO_REST_ENDPOINTS_BASE_URL +
                              userProfile.image_url
                            : "/images/user.png"
                        }
                        size="tiny"
                        circular
                        centered
                        style={{ marginBottom: "1rem" }}
                      />
                      {/* Name */}
                      <Card.Header
                        style={{ fontSize: "1.3rem", marginBottom: "0.5rem" }}
                      >
                        <Icon name="user" />
                        {userProfile.name || userProfile.full_name}
                      </Card.Header>
                      {/* Library ID */}
                      <Card.Meta
                        style={{ fontSize: "1rem", marginBottom: "1rem" }}
                      >
                        Library ID: <strong>{userProfile.library_id}</strong>
                      </Card.Meta>

                      {/* User Details */}
                      <Card.Description
                        style={{
                          fontSize: "0.9rem",
                          textAlign: "left",
                          marginBottom: "1.5rem",
                        }}
                      >
                        {userProfile.email && (
                          <div style={{ marginBottom: "0.5rem" }}>
                            <Icon name="mail" />
                            <strong>Email:</strong> {userProfile.email}
                          </div>
                        )}
                        {userProfile.program && (
                          <div style={{ marginBottom: "0.5rem" }}>
                            <Icon name="graduation cap" />
                            <strong>Program:</strong> {userProfile.program}
                          </div>
                        )}
                        {userProfile.year && (
                          <div style={{ marginBottom: "0.5rem" }}>
                            <Icon name="calendar" />
                            <strong>Year:</strong> {userProfile.year}
                          </div>
                        )}
                      </Card.Description>

                      {/* Purpose of Visit Section - Only show for check-in */}
                      {!attendanceStatus.is_checked_in && (
                        <div style={{ marginTop: "1rem" }}>
                          <Header
                            as="h4"
                            style={{ marginBottom: "0.8rem", color: "#225745" }}
                          >
                            <Icon name="question circle" />
                            Purpose of Visit
                          </Header>
                          <Form.Field>
                            <Dropdown
                              selection
                              value={purposeOfVisit}
                              options={purposeOptions}
                              onChange={(e, { value }) =>
                                setPurposeOfVisit(value)
                              }
                              style={dropdownStyle}
                              disabled={loading}
                              placeholder="Select purpose of visit"
                            />
                          </Form.Field>
                        </div>
                      )}

                      {/* Show current session purpose if checked in */}
                      {attendanceStatus.is_checked_in &&
                        attendanceStatus.session?.purposeOfVisit && (
                          <div style={{ marginTop: "1rem" }}>
                            <Label color="green" size="medium">
                              <Icon name="bullseye" />
                              Current Purpose:{" "}
                              {attendanceStatus.session.purposeOfVisit}
                            </Label>
                          </div>
                        )}
                    </Card.Content>
                  </Card>
                </Grid.Column>

                {/* Right Column: Attendance Status & Check In/Out */}
                <Grid.Column>
                  <Card
                    fluid
                    style={{ backgroundColor: "transparent", height: "100%" }}
                  >
                    <Card.Content>
                      <Card.Header
                        style={{
                          textAlign: "center",
                          fontSize: "1.3rem",
                          marginBottom: "1.5rem",
                        }}
                      >
                        <Icon name="clock" />
                        Attendance Status
                      </Card.Header>

                      <div
                        style={{ textAlign: "center", marginBottom: "1.5rem" }}
                      >
                        <Label
                          color={
                            attendanceStatus.is_checked_in ? "green" : "grey"
                          }
                          size="large"
                        >
                          <Icon
                            name={
                              attendanceStatus.is_checked_in
                                ? "check circle"
                                : "times circle"
                            }
                          />
                          {attendanceStatus.is_checked_in
                            ? "Currently Checked In"
                            : "Not Checked In"}
                        </Label>
                      </div>

                      {/* Current Session Info */}
                      {attendanceStatus.is_checked_in &&
                        attendanceStatus.session && (
                          <Segment
                            style={{
                              backgroundColor: "rgba(34, 87, 69, 0.1)",
                              border: "1px solid #225745",
                              borderRadius: "10px",
                              marginBottom: "1.5rem",
                            }}
                          >
                            <Header
                              size="small"
                              style={{
                                color: "#225745",
                                marginBottom: "0.5rem",
                              }}
                            >
                              Current Session:
                            </Header>
                            <p
                              style={{ fontSize: "0.9rem", margin: "0.3rem 0" }}
                            >
                              <Icon name="sign in" />
                              Checked in:{" "}
                              <strong>
                                {formatDateTime(
                                  attendanceStatus.session.inTime
                                )}
                              </strong>
                            </p>
                            {attendanceStatus.session.purposeOfVisit && (
                              <p
                                style={{
                                  fontSize: "0.9rem",
                                  margin: "0.3rem 0",
                                }}
                              >
                                <Icon name="bullseye" />
                                Purpose:{" "}
                                <strong>
                                  {attendanceStatus.session.purposeOfVisit}
                                </strong>
                              </p>
                            )}

                            {/* Auto checkout message */}
                            {loading && (
                              <Message
                                info
                                size="small"
                                style={{ marginTop: "1rem" }}
                              >
                                <Icon loading name="spinner" />
                                Automatically checking out...
                              </Message>
                            )}
                          </Segment>
                        )}

                      {/* Last Session Info */}
                      {!attendanceStatus.is_checked_in &&
                        attendanceStatus.last_session && (
                          <Segment
                            style={{
                              backgroundColor: "rgba(128, 128, 128, 0.1)",
                              border: "1px solid #cccccc",
                              borderRadius: "10px",
                              marginBottom: "1.5rem",
                            }}
                          >
                            <Header
                              size="small"
                              style={{ color: "#666", marginBottom: "0.5rem" }}
                            >
                              Last Session:
                            </Header>
                            <p
                              style={{ fontSize: "0.9rem", margin: "0.3rem 0" }}
                            >
                              <Icon name="history" />
                              {formatDateTime(
                                attendanceStatus.last_session.inTime
                              )}
                              {attendanceStatus.last_session.outTime
                                ? ` - Completed (${formatDateTime(
                                    attendanceStatus.last_session.outTime
                                  )})`
                                : " - Incomplete"}
                            </p>
                            {attendanceStatus.last_session.purposeOfVisit && (
                              <p
                                style={{
                                  fontSize: "0.9rem",
                                  margin: "0.3rem 0",
                                }}
                              >
                                <Icon name="bullseye" />
                                Purpose:{" "}
                                <strong>
                                  {attendanceStatus.last_session.purposeOfVisit}
                                </strong>
                              </p>
                            )}
                          </Segment>
                        )}

                      {/* Check In Button - Only show for users not checked in */}
                      {!attendanceStatus.is_checked_in && (
                        <div style={{ textAlign: "center" }}>
                          <Button
                            size="large"
                            color="green"
                            icon="sign in"
                            content="CHECK IN"
                            onClick={handleCheckIn}
                            disabled={loading || !purposeOfVisit}
                            loading={loading}
                            style={{
                              ...buttonStyle,
                              width: "100%",
                              padding: "1rem",
                            }}
                          />
                        </div>
                      )}
                    </Card.Content>
                  </Card>
                </Grid.Column>
              </Grid>
            )}
          </Segment>
        </div>
      </Container>
    </div>
  );
};

export default LibraryIdAttendance;
export { LibraryIdAttendance };
